import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  fetchSalesOrder,
  fetchSalesOrders,
  updateSalesOrder,
  type SalesOrderDetail,
  type SalesOrderSummary
} from "../../lib/sales-orders";
import { formatCurrency, formatDateTime } from "../../lib/utils";

type OrderStatus = "Pending" | "Paid" | "Processing" | "Delivered" | "Cancelled";
type PaymentType = "" | "MPESA" | "CASH" | "CHEQUE" | "BANK_TRANSFER";
type PaymentState = "all" | "unpaid" | "partial" | "paid";

type EditorState = {
  id: number | null;
  status: OrderStatus;
  amountPaid: string;
  paymentType: PaymentType;
  trackingNumber: string;
};

const emptyEditorState: EditorState = {
  id: null,
  status: "Pending",
  amountPaid: "0",
  paymentType: "",
  trackingNumber: ""
};

const paymentTypes: Exclude<PaymentType, "">[] = ["MPESA", "CASH", "CHEQUE", "BANK_TRANSFER"];

function paymentState(order: Pick<SalesOrderSummary, "total" | "amountPaid">) {
  if (order.total > 0 && order.amountPaid >= order.total) {
    return "paid";
  }
  if (order.amountPaid > 0) {
    return "partial";
  }
  return "unpaid";
}

function paymentStateLabel(state: PaymentState) {
  switch (state) {
    case "paid":
      return "Paid";
    case "partial":
      return "Part paid";
    case "unpaid":
      return "Unpaid";
    default:
      return "All";
  }
}

function paymentStateTone(state: PaymentState) {
  switch (state) {
    case "paid":
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300";
    case "partial":
      return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-300";
    case "unpaid":
      return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300";
    default:
      return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300";
  }
}

function paymentTypeLabel(value: PaymentType | string) {
  switch (value) {
    case "MPESA":
      return "M-Pesa";
    case "CASH":
      return "Cash";
    case "CHEQUE":
      return "Cheque";
    case "BANK_TRANSFER":
      return "Bank Transfer";
    default:
      return value || "Not recorded";
  }
}

function toEditorState(order: SalesOrderDetail): EditorState {
  return {
    id: order.id,
    status: (order.status as OrderStatus) || "Pending",
    amountPaid: String(order.amountPaid),
    paymentType: (order.paymentType as PaymentType) || "",
    trackingNumber: order.trackingNumber
  };
}

export function SalesPaymentsPage() {
  const [orders, setOrders] = useState<SalesOrderSummary[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<SalesOrderDetail | null>(null);
  const [query, setQuery] = useState("");
  const [paymentFilter, setPaymentFilter] = useState<PaymentState>("partial");
  const [methodFilter, setMethodFilter] = useState<PaymentType | "all">("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalOrders: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [loading, setLoading] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const metrics = useMemo(() => {
    const unpaid = orders.filter((order) => paymentState(order) === "unpaid");
    const partial = orders.filter((order) => paymentState(order) === "partial");
    const paid = orders.filter((order) => paymentState(order) === "paid");

    return [
      { label: "Visible payments", value: orders.length },
      { label: "Outstanding balance", value: formatCurrency(unpaid.concat(partial).reduce((sum, order) => sum + order.balance, 0)) },
      { label: "Part paid", value: partial.length },
      { label: "Paid in full", value: paid.length }
    ];
  }, [orders]);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("status", "all");
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchSalesOrders(params);
        let nextOrders = result.orders;

        if (paymentFilter !== "all") {
          nextOrders = nextOrders.filter((order) => paymentState(order) === paymentFilter);
        }
        if (methodFilter !== "all") {
          nextOrders = nextOrders.filter((order) => (order.paymentType || "") === methodFilter);
        }

        if (!ignore) {
          setOrders(nextOrders);
          setPagination(result.pagination);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load payments.");
          setOrders([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, [methodFilter, page, paymentFilter, query]);

  useEffect(() => {
    setPage(1);
  }, [methodFilter, paymentFilter, query]);

  async function openOrder(order: SalesOrderSummary) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const detail = await fetchSalesOrder(order.id);
      setSelectedOrder(detail);
      setEditor(toEditorState(detail));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load payment detail.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("status", "all");
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchSalesOrders(params);
    let nextOrders = result.orders;
    if (paymentFilter !== "all") {
      nextOrders = nextOrders.filter((order) => paymentState(order) === paymentFilter);
    }
    if (methodFilter !== "all") {
      nextOrders = nextOrders.filter((order) => (order.paymentType || "") === methodFilter);
    }
    setOrders(nextOrders);
    setPagination(result.pagination);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.id) {
      return;
    }

    const amountPaid = Number(editor.amountPaid);
    if (Number.isNaN(amountPaid) || amountPaid < 0) {
      setError("Amount paid must be a valid non-negative number.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateSalesOrder(editor.id, {
        status: editor.status,
        trackingNumber: editor.trackingNumber,
        amountPaid,
        paymentType: editor.paymentType
      });

      await reloadCurrentView();
      const refreshed = await fetchSalesOrder(editor.id);
      setSelectedOrder(refreshed);
      setEditor(toEditorState(refreshed));
      setSuccessMessage("Payment details saved successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save payment details.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales payments</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Collections desk</h1>
        <p className="mt-4 max-w-3xl text-nv-slate">
          Work unpaid and part-paid orders, confirm payment methods, and move collections cleanly toward fully settled revenue.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <div className="admin-card rounded-[28px] p-5" key={metric.label}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{metric.label}</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{metric.value}</p>
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(360px,1fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_220px]">
              <input
                className="field-control px-4 py-3 text-sm"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by order, customer, phone, address, or reference"
                value={query}
              />
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setPaymentFilter(event.target.value as PaymentState)} value={paymentFilter}>
                <option value="all">All payment states</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Part paid</option>
                <option value="paid">Paid</option>
              </select>
              <select className="field-control px-4 py-3 text-sm" onChange={(event) => setMethodFilter(event.target.value as PaymentType | "all")} value={methodFilter}>
                <option value="all">All methods</option>
                {paymentTypes.map((type) => (
                  <option key={type} value={type}>
                    {paymentTypeLabel(type)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}
          {successMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">{successMessage}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1080px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Order</th>
                    <th className="px-5 py-4">Customer</th>
                    <th className="px-5 py-4">Payment state</th>
                    <th className="px-5 py-4">Amounts</th>
                    <th className="px-5 py-4">Method</th>
                    <th className="px-5 py-4">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>Loading payments...</td></tr>
                  ) : orders.length > 0 ? (
                    orders.map((order) => {
                      const state = paymentState(order);
                      return (
                        <tr className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]" key={order.id} onClick={() => void openOrder(order)}>
                          <td className="px-5 py-4">
                            <p className="font-semibold text-nv-ink">#{order.id}</p>
                            <p className="mt-1 text-xs text-nv-slate">{formatDateTime(order.addedOn)}</p>
                            <p className="mt-2 text-sm text-nv-slate">{order.itemCount} items</p>
                          </td>
                          <td className="px-5 py-4 text-sm text-nv-slate">
                            <p className="font-semibold text-nv-ink">{order.deliveryName || order.email || "Walk-in / unknown"}</p>
                            <p className="mt-1">{order.deliveryPhone || order.buyerPhone || "No phone"}</p>
                            <p className="mt-1">{order.email || "No email"}</p>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${paymentStateTone(state)}`}>
                              {paymentStateLabel(state)}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-nv-slate">
                            <p className="font-semibold text-nv-ink">Total {formatCurrency(order.total)}</p>
                            <p className="mt-1">Paid {formatCurrency(order.amountPaid)}</p>
                            <p className="mt-1 font-semibold">Balance {formatCurrency(order.balance)}</p>
                          </td>
                          <td className="px-5 py-4 text-sm text-nv-slate">{paymentTypeLabel(order.paymentType)}</td>
                          <td className="px-5 py-4 text-sm text-nv-slate">{order.trackingNumber || "Not assigned"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>No payment items matched the current view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>Page {pagination.page} of {pagination.totalPages} · {pagination.totalOrders} orders</p>
              <div className="flex items-center gap-2">
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasPreviousPage || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">Previous</button>
                <button className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10" disabled={!pagination.hasNextPage || loading} onClick={() => setPage((current) => current + 1)} type="button">Next</button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{selectedOrder ? "Payment detail" : "Select payment"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">{selectedOrder ? `Order #${selectedOrder.id}` : "Collections editor"}</h2>
              </div>
              {editorOpen ? (
                <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                  Close
                </button>
              ) : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select an order to update the payment amount and payment method.
              </div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading payment detail...
              </div>
            ) : selectedOrder ? (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Customer</p>
                    <p className="mt-2 text-sm font-semibold text-nv-ink">{selectedOrder.deliveryName || selectedOrder.email || "Walk-in / unknown"}</p>
                    <p className="mt-1 text-sm text-nv-slate">{selectedOrder.deliveryPhone || selectedOrder.buyerPhone || "No phone"}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Payment state</p>
                    <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${paymentStateTone(paymentState(selectedOrder))}`}>
                      {paymentStateLabel(paymentState(selectedOrder))}
                    </span>
                    <p className="mt-2 text-sm text-nv-slate">Order status: {selectedOrder.status}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Order total</p>
                    <p className="mt-2 text-2xl font-semibold text-nv-ink">{formatCurrency(selectedOrder.total)}</p>
                  </div>
                  <div className="field-soft p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Outstanding balance</p>
                    <p className="mt-2 text-2xl font-semibold text-nv-ink">{formatCurrency(selectedOrder.balance)}</p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Amount paid</label>
                    <input
                      className="field-control px-4 py-3 text-sm"
                      min="0"
                      onChange={(event) => setEditor((current) => ({ ...current, amountPaid: event.target.value }))}
                      step="0.01"
                      type="number"
                      value={editor.amountPaid}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Payment method</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, paymentType: event.target.value as PaymentType }))} value={editor.paymentType}>
                      <option value="">Not recorded</option>
                      {paymentTypes.map((type) => (
                        <option key={type} value={type}>
                          {paymentTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Order status</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, status: event.target.value as OrderStatus }))} value={editor.status}>
                      {(["Pending", "Paid", "Processing", "Delivered", "Cancelled"] as OrderStatus[]).map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Reference / tracking</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, trackingNumber: event.target.value }))} value={editor.trackingNumber} />
                  </div>
                </div>

                <div className="rounded-[24px] border border-dashed border-black/10 p-4 text-sm text-nv-slate dark:border-white/10">
                  Fully paying the order will automatically reconcile its status to <span className="font-semibold text-nv-ink">Paid</span>.
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">
                    {saving ? "Saving..." : "Save payment"}
                  </button>
                  <a
                    className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold text-nv-ink dark:border-white/10"
                    href={`/sales/receipt?order=${selectedOrder.id}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Receipt
                  </a>
                  <a
                    className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold text-nv-ink dark:border-white/10"
                    href={`/sales/invoice?order=${selectedOrder.id}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    Invoice
                  </a>
                </div>
              </form>
            ) : (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Select an order to inspect and update its payment record.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
